/**
 * A terminology catalog can mix nested objects and flat dotted keys.
 * Example:
 * {
 *   "client": { "singular": "Paciente" },
 *   "welcome": "Hola, {name}"
 * }
 */
export type TerminologyValue = string | TerminologyDictionary;

export interface TerminologyDictionary {
  readonly [key: string]: TerminologyValue;
}

export type TerminologyParams = Readonly<
  Record<string, string | number | boolean | null | undefined>
>;

export const TermKey = {
  AccompanimentSingular: 'accompaniment.singular',
  AccompanimentPlural: 'accompaniment.plural',
  AccompanimentIndefinite: 'accompaniment.indefinite',
  AccompanimentDefinite: 'accompaniment.definite',
  OrganizationSingular: 'organization.singular',
  OrganizationPlural: 'organization.plural',
  OrganizationIndefinite: 'organization.indefinite',
  OrganizationDefinite: 'organization.definite',
  InventorySingular: 'inventory.singular',
  InventoryPlural: 'inventory.plural',
  InventoryIndefinite: 'inventory.indefinite',
  InventoryDefinite: 'inventory.definite',
  LocationSingular: 'location.singular',
  LocationPlural: 'location.plural',
  LocationIndefinite: 'location.indefinite',
  LocationDefinite: 'location.definite',
  ProfileSingular: 'profile.singular',
  ProfilePlural: 'profile.plural',
  ProfileIndefinite: 'profile.indefinite',
  ProfileDefinite: 'profile.definite',
  profilesSingular: 'profiles.singular',
  profilesPlural: 'profiles.plural',
  profilesIndefinite: 'profiles.indefinite',
  profilesDefinite: 'profiles.definite',
  ActivityServiceSingular: 'serviceActivity.singular',
  ActivityServicePlural: 'serviceActivity.plural',
  ActivityServiceIndefinite: 'serviceActivity.indefinite',
  ActivityServiceDefinite: 'serviceActivity.definite',
  AssistanceSingular: 'assistance.singular',
  AssistancePlural: 'assistance.plural',
  AssistanceIndefinite: 'assistance.indefinite',
  AssistanceDefinite: 'assistance.definite',
  SectorSingular: 'sector.singular',
  SectorPlural: 'sector.plural',
  SectorIndefinite: 'sector.indefinite',
  SectorDefinite: 'sector.definite',
  AddressSingular: 'address.singular',
  AddressPlural: 'address.plural',
  AddressIndefinite: 'address.indefinite',
  AddressDefinite: 'address.definite',
  ExpenseSingular: 'expense.singular',
  ExpensePlural: 'expense.plural',
  ExpenseIndefinite: 'expense.indefinite',
  ExpenseDefinite: 'expense.definite',
  SourcefoundingSingular: 'sourcefounding.singular',
  SourcefoundingPlural: 'sourcefounding.plural',
  SourcefoundingIndefinite: 'sourcefounding.indefinite',
  SourcefoundingDefinite: 'sourcefounding.definite',
  EmployeeSingular: 'employee.singular',
  EmployeePlural: 'employee.plural',
  EmployeeIndefinite: 'employee.indefinite',
  EmployeeDefinite: 'employee.definite',
  FixedAssetSingular: 'fixedAsset.singular',
  FixedAssetPlural: 'fixedAsset.plural',
  FixedAssetIndefinite: 'fixedAsset.indefinite',
  FixedAssetDefinite: 'fixedAsset.definite',
  ConsumableAssetSingular: 'consumableAsset.singular',
  ConsumableAssetPlural: 'consumableAsset.plural',
  ConsumableAssetIndefinite: 'consumableAsset.indefinite',
  ConsumableAssetDefinite: 'consumableAsset.definite',
  InfraestructureSingular: 'infraestructure.singular',
  InfraestructurePlural: 'infraestructure.plural',
  InfraestructureIndefinite: 'infraestructure.indefinite',
  InfraestructureDefinite: 'infraestructure.definite',
  AccompanimentServiceSingular: 'accompanimentService.singular',
  AccompanimentServicePlural: 'accompanimentService.plural',
  AccompanimentServiceIndefinite: 'accompanimentService.indefinite',
  AccompanimentServiceDefinite: 'accompanimentService.definite',
} as const;

export type KnownTerminologyKey = (typeof TermKey)[keyof typeof TermKey];

/**
 * Known keys get autocomplete; any other string remains valid at compile time.
 */
export type TerminologyKey = KnownTerminologyKey | (string & {});

export interface TerminologyResolveOptions {
  readonly fallback?: string;
  readonly params?: TerminologyParams;
}

export interface TerminologyLoadResult {
  readonly sector: string;
  readonly catalog: string;
  readonly terms: TerminologyDictionary;
  readonly fromCache: boolean;
  readonly usedFallback: boolean;
}
